import { useMutation } from '@apollo/client';
import type { UpdateObligationInternalAuditResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateObligationInternalAuditResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateObligationInternalAuditResultInput } from './useUpdateObligationInternalAuditResultTRPC';
import { useUpdateObligationInternalAuditResultTRPC } from './useUpdateObligationInternalAuditResultTRPC';

export const useUpdateObligationInternalAuditResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateGraphQL, graphqlState] = useMutation(
    UpdateObligationInternalAuditResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'obligation_internal_audit_result');
        evictField(cache, 'internal_audit_report');
        evictField(cache, 'obligation_internal_audit_result_aggregate');
      },
    }
  );

  const trpcMutation = useUpdateObligationInternalAuditResultTRPC();

  const updateObligationInternalAuditResult = async (
    variables: UpdateObligationInternalAuditResultInput
  ): Promise<UpdateObligationInternalAuditResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateObligationInternalAuditResult(variables);
    }

    const result = await updateGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to update obligation internal audit result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateObligationInternalAuditResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateObligationInternalAuditResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
